import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type MutationOptions,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

export interface ProjectContact {
  id: string;
  project_id: string;
  person_id: string;
  description: string;
  created_at: string;
  person?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string | null;
  };
}

type ZeroPerson = {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
};

type ZeroProjectContact = {
  id: string;
  projectID: string;
  personID: string;
  description: string;
  createdAt?: number | string | null;
  person?: ZeroPerson | null;
};

function toLegacyProjectContact(row: ZeroProjectContact): ProjectContact {
  return {
    id: row.id,
    project_id: row.projectID,
    person_id: row.personID,
    description: row.description,
    created_at: toLegacyTimestamp(row.createdAt),
    person: row.person
      ? {
          id: row.person.id,
          first_name: row.person.firstName,
          last_name: row.person.lastName,
          email: row.person.email ?? null,
          phone: row.person.phone ?? null,
        }
      : undefined,
  };
}

export function useProjectContacts(projectId: string | undefined) {
  const query = projectId
    ? appQueries.projectContacts.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });
  const contacts = ((data ?? []) as ZeroProjectContact[]).map(
    toLegacyProjectContact,
  );

  return {
    data: contacts,
    isLoading:
      !!projectId && details.type !== "complete" && contacts.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateProjectContact() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (contact: {
    project_id: string;
    person_id: string;
    description: string;
  }) =>
    runPending(async () => {
      const input = {
        id: generateUUID(),
        projectID: contact.project_id,
        personID: contact.person_id,
        description: contact.description,
      };
      const mutation = zero.mutate(appMutators.projectContacts.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Project contact create was rejected",
      );
      return toLegacyProjectContact(input);
    });

  return {
    mutate: (
      contact: {
        project_id: string;
        person_id: string;
        description: string;
      },
      options?: MutationOptions<ProjectContact>,
    ) => {
      void run(contact)
        .then((data) => {
          toast.success("Contact added");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to add contact: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateProjectContact() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = ({
    id,
    projectId,
    updates,
  }: {
    id: string;
    projectId: string;
    updates: { description?: string; person_id?: string };
  }) =>
    runPending(async () => {
      const input = {
        id,
        projectID: projectId,
        ...(updates.description !== undefined
          ? { description: updates.description }
          : {}),
        ...(updates.person_id !== undefined
          ? { personID: updates.person_id }
          : {}),
      };
      const mutation = zero.mutate(appMutators.projectContacts.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Project contact update was rejected",
      );
      return toLegacyProjectContact({
        ...input,
        personID: input.personID ?? "",
        description: input.description ?? "",
      });
    });

  return {
    mutate: (
      input: {
        id: string;
        projectId: string;
        updates: { description?: string; person_id?: string };
      },
      options?: MutationOptions<ProjectContact>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to update contact: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteProjectContact() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = ({ id, projectId }: { id: string; projectId: string }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.projectContacts.delete({ id, projectID: projectId }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Project contact delete was rejected",
      );
      return projectId;
    });

  return {
    mutate: (
      input: { id: string; projectId: string },
      options?: MutationOptions<string>,
    ) => {
      void run(input)
        .then((projectId) => {
          toast.success("Contact removed");
          options?.onSuccess?.(projectId);
        })
        .catch((error) =>
          toast.error("Failed to remove contact: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
